import { useState } from 'react';
import {
  buildShareCardBlob,
  downloadShareCard,
  getShareText,
  getShareUrl,
  type ShareCardOptions,
} from '../utils/shareCard';
import { t } from '../hooks/useT';

interface ShareSocialBlockProps {
  puzzleNumber: number;
  isArchive: boolean;
  zoom0ImageUrl: string | null | undefined;
  locale: string;
  scoreShareText?: string | null;
}

function encode(text: string) {
  return encodeURIComponent(text);
}

export function ShareSocialBlock({
  puzzleNumber,
  isArchive,
  zoom0ImageUrl,
  locale,
  scoreShareText,
}: ShareSocialBlockProps) {
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedScore, setCopiedScore] = useState(false);

  const cardOpts: ShareCardOptions | null = zoom0ImageUrl
    ? { zoom0ImageUrl, puzzleNumber, isArchive, locale }
    : null;

  const shareText = cardOpts ? getShareText(cardOpts) : '';
  const shareUrl = getShareUrl(puzzleNumber, isArchive);

  const openShare = async () => {
    if (!cardOpts) return;
    if (typeof navigator.share === 'function') {
      setBusy(true);
      try {
        const blob = await buildShareCardBlob(cardOpts);
        const file = new File([blob], `dezoom-${puzzleNumber}.png`, { type: 'image/png' });
        const payload: ShareData = {
          title: 'Dezoom',
          text: shareText,
          url: shareUrl,
        };
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ ...payload, files: [file] });
        } else {
          await navigator.share(payload);
        }
        return;
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
      } finally {
        setBusy(false);
      }
    }
    setPickerOpen(true);
  };

  const closePicker = () => setPickerOpen(false);

  const copyInvite = async () => {
    if (!shareText) return;
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent */
    }
  };

  const copyScore = async () => {
    if (!scoreShareText) return;
    try {
      await navigator.clipboard.writeText(scoreShareText);
      setCopiedScore(true);
      window.setTimeout(() => setCopiedScore(false), 2000);
    } catch {
      /* silent */
    }
  };

  const whatsappHref = `https://wa.me/?text=${encode(shareText)}`;
  const twitterHref = `https://twitter.com/intent/tweet?text=${encode(shareText)}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encode(shareUrl)}`;
  const blueskyHref = `https://bsky.app/intent/compose?text=${encode(shareText)}`;

  if (!cardOpts) return null;

  return (
    <>
      <section className="share-social-block">
        <button
          type="button"
          className="btn primary share-social-main-btn"
          disabled={busy}
          onClick={() => void openShare()}
        >
          {busy ? t('common.loading') : t('share.button')}
        </button>
        {scoreShareText && (
          <div className="share-score-row">
            <button type="button" className="btn secondary admin-btn-sm" onClick={() => void copyScore()}>
              {copiedScore ? t('result.copied') : t('share.copyScore')}
            </button>
            <pre className="share-preview">{scoreShareText}</pre>
          </div>
        )}
      </section>

      {pickerOpen && (
        <div className="admin-crop-overlay" role="dialog" aria-modal="true" onClick={closePicker}>
          <div className="share-picker-modal" onClick={(e) => e.stopPropagation()}>
            <header className="admin-crop-head">
              <h2>{t('share.pickerTitle')}</h2>
            </header>
            <div className="share-picker-actions">
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="btn secondary block" onClick={closePicker}>
                {t('share.whatsapp')}
              </a>
              <a href={twitterHref} target="_blank" rel="noopener noreferrer" className="btn secondary block" onClick={closePicker}>
                {t('share.twitter')}
              </a>
              <a href={facebookHref} target="_blank" rel="noopener noreferrer" className="btn secondary block" onClick={closePicker}>
                {t('share.facebook')}
              </a>
              <a href={blueskyHref} target="_blank" rel="noopener noreferrer" className="btn secondary block" onClick={closePicker}>
                {t('share.bluesky')}
              </a>
              <button
                type="button"
                className="btn secondary block"
                disabled={busy}
                onClick={() => {
                  void downloadShareCard(cardOpts);
                  closePicker();
                }}
              >
                {t('share.download')}
              </button>
              <button type="button" className="btn ghost block" onClick={() => void copyInvite()}>
                {copied ? t('result.copied') : t('share.copyLink')}
              </button>
            </div>
            <button type="button" className="btn ghost block share-picker-close" onClick={closePicker}>
              {t('share.pickerClose')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
